import { useState, useEffect, useCallback } from 'react';
import { VendedorMetrics, VendedorDetailedMetrics, VendedorFilters } from '@/types/vendedores';
import { supabase } from '@/integrations/supabase/client';

export interface Vendedor {
  id: string;
  nome: string;
}

/**
 * Hook customizado para buscar vendedores com métricas agregadas
 * Aceita filtros para personalizar a busca
 */
export function useVendedores(filters?: VendedorFilters) {
  const [vendedores, setVendedores] = useState<VendedorMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVendedores = useCallback(async () => {
    try {
      setLoading(true);

      // Fazer requisição para a API do backend (usando proxy do Vite)
      // O fetchWithAuth automaticamente adiciona o header Authorization
      const response = await fetch('/api/vendedores', {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao buscar vendedores');
      }

      const data: VendedorMetrics[] = await response.json();

      if (import.meta.env.DEV) {
        console.log('🔍 useVendedores - Vendedores carregados:', data);
      }

      // Aplicar filtros localmente se necessário
      let filteredData = data;

      if (filters) {
        // Filtrar por status
        if (filters.status === 'ativo') {
          filteredData = filteredData.filter(v => v.status === true);
        } else if (filters.status === 'inativo') {
          filteredData = filteredData.filter(v => v.status === false);
        }

        // Ordenar
        if (filters.ordenacao) {
          filteredData = [...filteredData].sort((a, b) => {
            switch (filters.ordenacao) {
              case 'nome':
                return (a.vendedor_nome || '').localeCompare(b.vendedor_nome || '');
              case 'data_inicio':
                return new Date(b.data_inicio).getTime() - new Date(a.data_inicio).getTime();
              case 'total_parceiros':
                return b.total_parceiros - a.total_parceiros;
              case 'total_leads':
                return b.total_leads - a.total_leads;
              case 'valor_contratos':
                return b.valor_contratos - a.valor_contratos;
              case 'taxa_conversao':
                return b.taxa_conversao_leads_contratos - a.taxa_conversao_leads_contratos;
              default:
                return 0;
            }
          });
        }
      }

      setVendedores(filteredData);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar vendedores:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchVendedores();
  }, [fetchVendedores]);

  return { vendedores, loading, error, refetch: fetchVendedores };
}

/**
 * Hook customizado para buscar detalhes de um vendedor específico
 * Usa a API do backend para obter dados completos com parceiros e métricas
 */
export function useVendedorDetails(vendedorId: string | null) {
  const [vendedor, setVendedor] = useState<VendedorDetailedMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVendedor = useCallback(async () => {
    if (!vendedorId) {
      setVendedor(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Buscar dados completos do vendedor via API do backend
      const response = await fetch(`/api/vendedores/${vendedorId}/metrics`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao buscar detalhes do vendedor');
      }

      const data = await response.json();

      if (import.meta.env.DEV) {
        console.log('🔍 useVendedorDetails - Dados do vendedor:', data);
      }

      // Mapear resposta da API para o formato VendedorDetailedMetrics
      // Usar metrics_agregadas que inclui leads diretos do vendedor + leads dos parceiros
      const metrics = data.metrics_agregadas || {};
      
      const vendedorData: VendedorDetailedMetrics = {
        vendedor_id: data.vendedor.id,
        vendedor_nome: data.vendedor.nome,
        vendedor_telefone: data.vendedor.telefone || '',
        vendedor_email: data.vendedor.email || '',
        vendedor_cpf: data.vendedor.cpf || '',
        vendedor_pix: data.vendedor.pix || '',
        vendedor_cidade: data.vendedor.cidade || '',
        vendedor_estado: data.vendedor.estado || '',
        data_inicio: data.vendedor.created_at,
        status: data.vendedor.ativo || false,
        
        // Métricas de parceiros
        total_parceiros: metrics.total_parceiros || 0,
        parceiros_ativos: metrics.parceiros_ativos || 0,
        parceiros_inativos: (metrics.total_parceiros || 0) - (metrics.parceiros_ativos || 0),
        parceiros_30_dias: 0, // Não disponível nesta API
        
        // Métricas de leads (agregado: parceiros + vendedor direto)
        total_leads: metrics.total_leads || 0,
        leads_30_dias: 0, // Não disponível nesta API
        leads_7_dias: 0, // Não disponível nesta API
        
        // Métricas de propostas
        propostas_enviadas: metrics.propostas_enviadas || 0,
        propostas_30_dias: 0, // Não disponível nesta API
        valor_propostas: metrics.valor_propostas || 0,
        valor_propostas_30_dias: 0, // Não disponível nesta API
        
        // Métricas de contratos
        contratos_fechados: metrics.contratos_fechados || 0,
        contratos_30_dias: 0, // Não disponível nesta API
        valor_contratos: metrics.valor_contratos || 0,
        valor_contratos_30_dias: 0, // Não disponível nesta API
        
        // Métricas Solares
        total_placas: metrics.total_placas || 0,
        potencia_instalada: metrics.potencia_instalada || 0,
        lucro_por_placa: metrics.lucro_por_placa || 0,
        
        // Taxas de conversão (já calculadas pela API)
        taxa_conversao_leads_contratos: metrics.taxa_conversao_leads_contratos || 0,
        taxa_conversao_leads_propostas: metrics.taxa_conversao_leads_propostas || 0,
        
        // Parceiros e métricas por período
        parceiros: data.parceiros || [],
        metrics_por_periodo: data.metrics_por_periodo || []
      };

      if (import.meta.env.DEV) {
        console.log('📊 Métricas do vendedor:', {
          total_leads: vendedorData.total_leads,
          leads_parceiros: metrics.total_leads_parceiros,
          leads_vendedor_direto: metrics.total_leads_vendedor_direto,
          propostas: vendedorData.propostas_enviadas,
          contratos: vendedorData.contratos_fechados
        });
      }

      setVendedor(vendedorData);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar detalhes do vendedor:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      setVendedor(null);
    } finally {
      setLoading(false);
    }
  }, [vendedorId]);

  useEffect(() => {
    fetchVendedor();
  }, [fetchVendedor]);

  return { vendedor, loading, error, refetch: fetchVendedor };
}
